import Image from "next/image";
import { useEffect, useState } from "react";
import { GoPlus } from "react-icons/go";
import { IoMdClose } from "react-icons/io";
import { TbMinus } from "react-icons/tb";

import { toCdnUrl } from "../../lib/cdn";
import { resolveImage } from "../../lib/media";
import { cn } from "../../lib/utils";
import Picture from "../Picture";
import { parseFileName } from "../utils/fileutil";
import styles from "./bag-item.module.css";

const BagItem = ({ item, removeProductFromCart, updateProductCount }) => {
  const [count, setCount] = useState(item.quantity || 1);
  const [availableStock, setAvailableStock] = useState(0);
  const [mounted, setMounted] = useState(false);
  const availableProductQuantity = item?.productCount
    ? parseInt(item?.productCount)
    : Number.MAX_SAFE_INTEGER;
  const isProductCustomised = item.customAttributes.length > 0;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    updateProductCount(item.id, item.size, count);
    if (item.size) {
      const stock = findProductStockAndPrice(item.size, item);
      setAvailableStock(stock && stock.stockAvailable ? stock.stockAvailable : 0);
    } else {
      setAvailableStock(item.availableStock);
    }
  }, [count]);

  useEffect(() => {
    if (mounted && item?.fields?.["Other photos"]?.length) {
      localStorage.clear();
      window.location.reload();
    }
  }, [mounted, item]);

  const findProductStockAndPrice = (size, product) => {
    if (!size) {
      size = "-";
    }
    const variantPrice = product.variantOptions;
    const variantObj = {};

    if (!variantPrice || (variantPrice && !variantPrice.length)) {
      return product.price;
    }

    variantPrice.forEach((variant) => {
      let vSize = variant && variant.type === "fit" ? variant?.name : "-";

      if (!variantObj[vSize]) {
        variantObj[vSize] = {
          price: variant?.price,
          stockAvailable: variant?.availableStock,
        };
      }
    });

    return variantObj[size];
  };

  const originalPhoto = item?.productPhotos[0];
  const urlPath = originalPhoto ? new URL(originalPhoto).pathname : "";
  const extractedImageName = parseFileName(urlPath).name;
  const newPhoto = originalPhoto?.includes("dm2buy")
    ? `https://dm2buy-resize-dynamic-cebdcaefgydgh6hu.z02.azurefd.net/dm2buy/${extractedImageName}.jpg?quality=50`
    : toCdnUrl(originalPhoto);
  const fallBackPhoto = originalPhoto;
  const resolved = resolveImage(originalPhoto, item?.productPhotosV2, "cart");

  if (!mounted) {
    return null;
  }

  return (
    <div className={styles.orderItem}>
      <div className={styles.productDetails}>
        <div className={styles.thumbnailContainer}>
          {resolved.isV2 ? (
            <Picture
              resolved={resolved}
              alt="Order name"
              width={60}
              height={60}
              imgClassName={styles.orderThumbnail}
            />
          ) : (
            <Image
              src={newPhoto}
              unoptimized={true}
              height={60}
              width={60}
              alt="Order name"
              className={styles.orderThumbnail}
              onError={(e) => {
                e.target.src = fallBackPhoto;
              }}
            />
          )}

          {item.colour ? (
            <div className={styles.productColor} style={{ backgroundColor: item.colour }}></div>
          ) : null}
        </div>

        <div className={styles.productName}>
          <span>{item.name}</span>

          {isProductCustomised && (
            <div className={styles.product_specs}>
              {item.customAttributes
                .map((ca) => (
                  <span key={`${ca.name}-${ca.value}`}>
                    {ca.name} - {ca.value + ""}
                  </span>
                ))
                .reduce((prev, curr) => [prev, ", ", curr])}
            </div>
          )}

          <div className={styles.product_specs}>{item.size ? "Variant - " + item.size : ""}</div>

          {availableProductQuantity > 1 && (
            <div className={styles.quantityControls}>
              <button
                className="rounded-full p-2"
                onClick={() => {
                  if (count < 2) return;
                  setCount(count - 1);
                }}
                aria-label="Decrement quantity"
              >
                <TbMinus size={20} className="text-fontBackground" />
              </button>

              <span>{count}</span>

              <button
                className="rounded-full p-2"
                onClick={() => {
                  if (count < availableStock) {
                    setCount(count + 1);
                  }
                }}
                aria-label="Increment quantity"
              >
                <GoPlus size={20} className="text-fontBackground" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.details_right}>
        <div className={cn(styles.productPrice, "!text-fontBackground")}>
          {`${String.fromCharCode(0x20b9)}${count * item.price}`}
        </div>

        <button
          onClick={removeProductFromCart(item.id)}
          className="rounded-full p-2"
          aria-label="Remove product from cart"
        >
          <IoMdClose size={20} className="text-fontBackground" />
        </button>
      </div>
    </div>
  );
};

export default BagItem;
